"use client"

import { useEffect, useState } from "react"
import { useLanguage } from "./LanguageContext"
import { loadAppConfiguration } from "./ContentLoader"
import Navigation, { Footer } from "./Navigation"
import AboutPage from "./AboutPage"
import LessonLauncher from "./LessonLauncher"
import MissiesBanner from "./components/MissiesBanner"
import "./css/AboutPage.css"


const GameSelector = () => {
  const { language } = useLanguage()

  const [appConfig, setAppConfig] = useState(null)
  const [showAboutPage, setShowAboutPage] = useState(false)
  const [aboutContent, setAboutContent] = useState(null)
  const [isBetaMode, setIsBetaMode] = useState(false)

  const t = (path, defaultText = "") => {
    if (!appConfig) return defaultText

    const keys = path.split(".")
    let value = appConfig
    for (const key of keys) {
      if (!value || !value[key]) return defaultText
      value = value[key]
    }

    if (value && typeof value === "object" && value[language.code]) return value[language.code]
    return typeof value === "string" ? value : value?.en || defaultText
  }

  const handleNavigation = (page) => {
    setShowAboutPage(page === "about")
  }

  useEffect(() => {
    const checkBetaMode = () => {
      const isBeta = window.location.pathname.includes("/beta")
      setIsBetaMode(isBeta)
    }
    checkBetaMode()
    window.addEventListener("popstate", checkBetaMode)
    window.addEventListener("hashchange", checkBetaMode)
    return () => {
      window.removeEventListener("hashchange", checkBetaMode)
      window.removeEventListener("popstate", checkBetaMode)
    }
  }, [])

  useEffect(() => {
    const fetchAppConfig = async () => {
      try {
        const companyId = new URLSearchParams(window.location.search).get("company") || null
        const config = await loadAppConfiguration(language.code, companyId, isBetaMode)
        setAppConfig(config)

        if (config?.branding?.accentColor) {
          document.documentElement.style.setProperty("--accent-color", config.branding.accentColor)
        }
      } catch (error) {
        console.error("Failed to load app configuration:", error)
      }
    }

    fetchAppConfig()
  }, [language.code, isBetaMode])

  useEffect(() => {
    if (aboutContent) return

    const fetchAboutContent = async () => {
      try {
        const response = await fetch(`/content/main/app-about.json`)
        if (!response.ok) throw new Error(`Failed to load about content: ${response.status}`)
        const data = await response.json()
        setAboutContent(data)
      } catch (error) {
        console.error("Failed to load about content:", error)
      }
    }

    fetchAboutContent()
  }, [showAboutPage, aboutContent])

  // About page
  if (showAboutPage) {
    return (
      <div className="game-selector-container">
        <Navigation appConfig={appConfig} currentPage="about" onNavigate={handleNavigation} t={t} />

       <main className="main-content">
  {/* Hide banner on overview */}
  {!String(mode).toLowerCase().includes("overview") && (
    <section className="hero-section">
      <div className="hero-content">
        <div className="hero-content-inner">
          <h1 className="hero-title">{t("heroSection.lessonHeader", "Lessons")}</h1>
          <p className="hero-subtitle">
            {t("heroSection.lessonSubheader", "Bekijk de opbouw, planning en start daarna de les.")}
          </p>
        </div>
      </div>
    </section>
  )}

  <section className={`games-section ${String(mode).toLowerCase().includes("overview") ? "games-section--overview" : ""}`}>
    {children}
  </section>
</main>


        <Footer appConfig={appConfig} t={t} />
      </div>
    )
  }

  // Lessons page: header/footer ONLY for grid/preview/password
  // Lessons page: header/footer ONLY for grid/preview/password
return (
  <LessonLauncher
    languageCode={language.code}
    beta={isBetaMode}
    variant="lessons"
    enableDeepLink={true}
    renderShell={(children, mode) => {
      // ✅ Same approach as LandingPage:
      // If we're NOT on the grid, we are in overview/player/etc.
      // => hide header + hero entirely.
      if (mode !== "grid") {
        return (
          <div className="game-selector-container">
                      <Navigation appConfig={appConfig} currentPage="lessons" onNavigate={handleNavigation} t={t} />

            <main className="main-content">{children}</main>
                      <Footer appConfig={appConfig} t={t} />

          </div>
        )
      }

      // ✅ Grid view: show normal /lessons page with nav + hero + footer
      return (
        <div className="game-selector-container">
          <Navigation appConfig={appConfig} currentPage="lessons" onNavigate={handleNavigation} t={t} />

          <main className="main-content">
            <MissiesBanner data={aboutContent?.missiesBanner} languageCode={language.code} />

            <section className="hero-section">
              <div className="hero-content">
                <div className="hero-content-inner">
                  <h1 className="hero-title">{t("heroSection.lessonHeader", "Lessons")}</h1>
                  <p className="hero-subtitle">
                    {t("heroSection.lessonSubheader", "Bekijk de opbouw, planning en start daarna de les.")}
                  </p>
                </div>
              </div>
            </section>

<section className="games-section">
  {children}

  {/* ✅ Full-width CTA (matches landing page styling) */}
   <section className="exit-lesson-about">
              <div className="about-me-container">
  <div className="exit-banner">
    
    <div className="coming-soon-section">
      <h1 className="coming-soon-title">
        {t("lessonsExitBanner.title", language.code === "nl" ? "Klaar om te starten?" : "Ready to get started?")}
      </h1>

      <p className="hero-subtitle">
        {t(
          "lessonsExitBanner.text",
          language.code === "nl"
            ? "Word een 'Founding Family' voor €49 per jaar (normaal €79) in ruil voor je feedback. Inclusief onbeperkte toegang tot álle leeravonturen voor het hele gezin (incl. 21% btw)."
            : "Become a 'Founding Family' for €49 per year (normally €79) in exchange for your feedback. Includes unlimited access to all learning adventures for the whole family (incl. 21% VAT).",
        )}
      </p>

      <a
        className="main-action-button"
        href={t("lessonsExitBanner.href", "/portal/register.php")}
      >
        {t(
          "lessonsExitBanner.button",
          language.code === "nl" ? "Start met Techtok" : "Start Techtok",
        )}
      </a>
    </div>
  </div>
  </div>
</section>

</section>
          </main>

          <Footer appConfig={appConfig} t={t} />
        </div>
      )
    }}
  />
)

}

export default GameSelector
